import { create } from 'zustand';
import {
  PROTOCOL_VERSION,
  type Avatar,
  type Facing,
  type Member,
  type ReactionEmoji,
  type RoomInfo,
  type ServerMessage,
} from '@sokdaq/protocol';
import { RoomConnection, type ConnectionStatus } from '../net/connection.js';
import { roomSocketUrl } from '../net/endpoint.js';
import { uuid } from '../net/uuid.js';
import {
  applyReaction,
  buildReactionMap,
  mergeMessages,
  upsertMember,
  upsertMessage,
  type DisplayMessage,
  type ReactionMap,
} from './message-list.js';
import { rekeyMemberToken, saveLastRoom, saveMemberToken } from './persist.js';

export type { DisplayMessage };

/** 타이핑 표시를 자동으로 지우는 시간. */
const TYPING_TIMEOUT_MS = 6_000;

export interface ChatState {
  status: ConnectionStatus;
  statusDetail: string | null;

  room: RoomInfo | null;
  /**
   * 지금 쓰고 있는 초대코드.
   *
   * 화면에 보여주고 사람을 부를 때 쓴다. 방장이 회전시키면 여기도 바뀌므로
   * 화면 밖에서 들고 다니지 않는다 — 낡은 코드를 복사해 전달하게 된다.
   */
  code: string | null;
  you: Member | null;
  members: readonly Member[];
  messages: readonly DisplayMessage[];
  typingUserIds: readonly string[];
  /**
   * 오브젝트 상호작용 이모트. userId → 무엇을 언제.
   *
   * 오피스 뷰가 머리 위에 잠깐 띄운다. 화면 표현일 뿐이라 저장하지 않는다.
   */
  emotes: Readonly<Record<string, { objectId: string; at: number }>>;
  /** 메시지별 리액션. msgId → 이모지 → 누른 사람들. */
  reactions: ReactionMap;
  /** 마지막으로 확정 수신한 seq. 재접속 시 델타의 기준점이 된다. */
  latestSeq: number;

  enter(code: string, nickname: string, avatar: Avatar, memberToken: string | null): void;
  leave(): void;

  sendText(body: string): void;
  sendWhisper(body: string): void;
  move(x: number, y: number, facing: Facing): void;
  interact(objectId: string): void;
  toggleReaction(msgId: string, emoji: ReactionEmoji): void;
  updateProfile(patch: { nickname?: string; avatar?: Avatar }): void;

  /** 아래 넷은 방장만 쓸 수 있다. 서버가 다시 확인한다. */
  updateRoom(patch: { name?: string; locked?: boolean }): void;
  rotateCode(): void;
  kick(userId: string): void;

  editMessage(id: string, body: string): void;
  deleteMessage(id: string): void;
  setTyping(on: boolean): void;
}

/**
 * 스토어 밖에 두는 것들.
 *
 * 연결과 타이머는 렌더링에 쓰이지 않는다. 스토어에 넣으면 불필요한
 * 리렌더를 유발하고 직렬화도 되지 않는다.
 */
let connection: RoomConnection | null = null;
let currentCode: string | null = null;
const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const useChatStore = create<ChatState>((set, get) => {
  function apply(message: ServerMessage): void {
    switch (message.t) {
      case 'welcome': {
        void saveMemberToken(currentCode!, message.memberToken);
        void saveLastRoom(currentCode!);

        set((state) => ({
          room: message.room,
          you: message.you,
          members: message.members,
          // 델타(sinceSeq를 보낸 재접속)면 기존 목록에 이어 붙이고,
          // 최초 입장이면 받은 것으로 대체한다.
          messages:
            state.latestSeq > 0
              ? mergeMessages(state.messages, message.messages)
              : message.messages,
          // 리액션은 서버가 보내준 것으로 통째로 갈아끼운다. 델타가 아니라
          // 최근 구간 전체이므로 합치면 이미 취소된 것이 되살아난다.
          reactions: buildReactionMap(message.reactions),
          latestSeq: Math.max(state.latestSeq, message.latestSeq),
        }));
        return;
      }

      case 'message': {
        set((state) => ({
          messages: upsertMessage(state.messages, message.message),
          // 속닥은 저장되지 않아 seq가 0이다. 기준점을 뒤로 돌리면 안 된다.
          latestSeq: Math.max(state.latestSeq, message.message.seq),
        }));
        return;
      }

      case 'messageUpdated': {
        set((state) => ({
          messages: state.messages.map((m) =>
            m.id === message.id ? { ...m, body: message.body, editedAt: message.editedAt } : m,
          ),
        }));
        return;
      }

      case 'messageDeleted': {
        set((state) => ({
          messages: state.messages.map((m) =>
            m.id === message.id ? { ...m, body: '', deletedAt: message.deletedAt } : m,
          ),
        }));
        return;
      }

      case 'roomUpdated': {
        set({ room: message.room });
        return;
      }

      case 'codeRotated': {
        /*
         * 저장된 것들이 전부 구코드에 묶여 있다. 옮기지 않으면 다음
         * 재접속이 죽은 코드로 나가 404를 맞고, 자동 복귀도 실패한다.
         */
        const previous = currentCode;
        currentCode = message.code;
        if (previous !== null) void rekeyMemberToken(previous, message.code);
        void saveLastRoom(message.code);
        set({ code: message.code });
        return;
      }

      case 'memberJoined':
      case 'memberUpdated': {
        set((state) => ({
          members: upsertMember(state.members, message.member),
          // 나에 대한 갱신이면 you도 함께 바꾼다. 프레즌스와 같은 함정이다 —
          // members만 고치면 화면 대부분은 멀쩡한데 you를 읽는 쪽(설정 패널의
          // 아바타 선택 표시 같은 것)만 조용히 옛 값을 본다.
          you:
            state.you !== null && state.you.userId === message.member.userId
              ? message.member
              : state.you,
        }));
        return;
      }

      case 'memberLeft': {
        set((state) => ({
          members: state.members.map((m) =>
            m.userId === message.userId ? { ...m, online: false } : m,
          ),
          typingUserIds: state.typingUserIds.filter((id) => id !== message.userId),
        }));
        return;
      }

      case 'presence': {
        set((state) => {
          const byId = new Map(message.updates.map((u) => [u.userId, u]));
          const move = <T extends { userId: string; x: number; y: number; facing: Facing }>(m: T): T => {
            const update = byId.get(m.userId);
            return update ? { ...m, x: update.x, y: update.y, facing: update.facing } : m;
          };

          // you도 함께 갱신한다. 빼먹으면 입장 시점 좌표에 영원히 멈춰
          // 있는 사본이 남는다. 오피스 뷰는 자기 위치를 따로 예측하므로
          // 화면은 멀쩡해 보이지만, you.x를 읽는 쪽은 조용히 틀린 값을 본다.
          return {
            members: state.members.map(move),
            you: state.you === null ? null : move(state.you),
          };
        });
        return;
      }

      case 'typing': {
        setTypingFlag(message.userId, message.on);
        return;
      }

      case 'error': {
        console.warn(`서버 오류 [${message.code}] ${message.message}`);
        // 낙관적으로 그려둔 메시지가 거부됐다면 화면에 남겨두면 안 된다.
        // 어떤 메시지였는지는 서버가 알려주지 않으므로, 확정되지 못한
        // 항목은 사용자가 재전송할 수 있도록 실패 표시만 남긴다.
        set({ statusDetail: message.message });
        return;
      }

      case 'reaction': {
        set((state) => ({
          reactions: applyReaction(
            state.reactions,
            message.msgId,
            message.emoji,
            message.userId,
            message.on,
          ),
        }));
        return;
      }

      case 'interact': {
        set((state) => ({
          emotes: { ...state.emotes, [message.userId]: { objectId: message.objectId, at: Date.now() } },
        }));
        return;
      }

      case 'pong':
        // 연결 유지용이라 상태가 없다.
        return;
    }
  }

  function setTypingFlag(userId: string, on: boolean): void {
    const existing = typingTimers.get(userId);
    if (existing !== undefined) clearTimeout(existing);
    typingTimers.delete(userId);

    if (!on) {
      set((state) => ({ typingUserIds: state.typingUserIds.filter((id) => id !== userId) }));
      return;
    }

    set((state) =>
      state.typingUserIds.includes(userId)
        ? state
        : { typingUserIds: [...state.typingUserIds, userId] },
    );

    // 타이핑 중에 연결이 끊기면 off가 오지 않는다. 표시가 영원히
    // 남지 않도록 스스로 만료시킨다.
    typingTimers.set(
      userId,
      setTimeout(() => setTypingFlag(userId, false), TYPING_TIMEOUT_MS),
    );
  }

  function sendOptimistic(kind: 'text' | 'whisper', body: string): void {
    const you = get().you;
    if (connection === null || you === null) return;

    const trimmed = body.trim();
    if (trimmed === '') return;

    const id = uuid();
    const optimistic: DisplayMessage = {
      seq: 0,
      id,
      userId: you.userId,
      kind,
      body: trimmed,
      zone: null,
      createdAt: Date.now(),
      editedAt: null,
      deletedAt: null,
      pending: true,
    };

    set((state) => ({ messages: [...state.messages, optimistic] }));
    connection.send({ t: 'send', id, kind, body: trimmed });
  }

  return {
    status: 'closed',
    statusDetail: null,
    room: null,
    code: null,
    you: null,
    members: [],
    messages: [],
    typingUserIds: [],
    emotes: {},
    reactions: {},
    latestSeq: 0,

    enter(code, nickname, avatar, memberToken) {
      get().leave();
      currentCode = code;

      set({
        status: 'connecting',
        statusDetail: null,
        room: null,
        code,
        you: null,
        members: [],
        messages: [],
        typingUserIds: [],
        emotes: {},
        reactions: {},
        latestSeq: 0,
      });

      connection = new RoomConnection({
        // 매번 지금의 코드를 묻는다. 회전하면 currentCode가 바뀐다.
        url: () => roomSocketUrl(currentCode ?? code),
        // 재접속마다 다시 호출된다. 그때의 최신 seq가 실려야
        // 놓친 구간만 받아올 수 있다.
        makeHello: () => {
          const { latestSeq } = get();
          return {
            t: 'hello',
            v: PROTOCOL_VERSION,
            nickname,
            avatar,
            ...(memberToken !== null ? { memberToken } : {}),
            ...(latestSeq > 0 ? { sinceSeq: latestSeq } : {}),
          };
        },
        onMessage: apply,
        onStatus: (status, detail) => set({ status, statusDetail: detail ?? null }),
      });

      connection.connect();
    },

    leave() {
      connection?.dispose();
      connection = null;
      currentCode = null;
      for (const timer of typingTimers.values()) clearTimeout(timer);
      typingTimers.clear();
      set({
        status: 'closed',
        room: null,
        code: null,
        you: null,
        members: [],
        messages: [],
        typingUserIds: [],
        emotes: {},
        reactions: {},
        latestSeq: 0,
      });
    },

    sendText: (body) => sendOptimistic('text', body),
    sendWhisper: (body) => sendOptimistic('whisper', body),

    move(x, y, facing) {
      // 스로틀은 호출부(렌더 루프)가 한다. 여기서 또 걸면 마지막 좌표를
      // 놓쳐서 아바타가 걷다 만 자리에 멈춰 보인다.
      connection?.send({ t: 'move', x, y, facing });
    },

    interact(objectId) {
      connection?.send({ t: 'interact', objectId });
    },

    toggleReaction(msgId, emoji) {
      const { you, reactions } = get();
      if (you === null) return;

      // 지금 눌려 있는지 보고 반대로 보낸다. 서버가 확정 결과를
      // 브로드캐스트하므로 여기서 낙관적으로 그리지는 않는다 —
      // 리액션은 즉시성이 덜 중요하고, 어긋나면 더 헷갈린다.
      const mine = reactions[msgId]?.[emoji]?.includes(you.userId) === true;
      connection?.send({ t: 'react', msgId, emoji, on: !mine });
    },

    updateProfile(patch) {
      connection?.send({ t: 'profile', ...patch });
    },

    updateRoom(patch) {
      connection?.send({ t: 'room', ...patch });
    },

    rotateCode() {
      connection?.send({ t: 'rotateCode' });
    },

    kick(userId) {
      connection?.send({ t: 'kick', userId });
    },

    editMessage(id, body) {
      const trimmed = body.trim();
      if (trimmed === '') return;
      connection?.send({ t: 'edit', id, body: trimmed });
    },

    deleteMessage(id) {
      connection?.send({ t: 'delete', id });
    },

    setTyping(on) {
      connection?.send({ t: 'typing', on });
    },
  };
});

